import * as React from 'react'
import { useAudioRecorder } from 'react-audio-voice-recorder'
import { styled } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMicrophone } from '@fortawesome/pro-solid-svg-icons'
import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import { ControlButton } from '~/components/Buttons'

export interface Props {
  onIsRecording: (isRecording: boolean) => void
  onRecordingComplete: (blob: Blob) => void
  maxRecordingTime?: number
  disabled: boolean
  eventKey: string
}

const Container = styled.div`
  display: flex;
  flex: 1;
  gap: 0.5rem;

  height: 100%;
  justify-content: flex-end;

  > ${ControlButton} {
    font-size: 1.25rem;

    display: flex;
    justify-content: center;
    align-items: center;

    width: 2.5rem;
    height: 100%;
  }
`

export const View: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  onIsRecording,
  onRecordingComplete,
  maxRecordingTime = 30,
  disabled = false
}) => {
  const { isRecording, startRecording, stopRecording, recordingBlob, recordingTime } = useAudioRecorder(
    {
      noiseSuppression: true,
      echoCancellation: true
    },
    e => console.error(e),
    {
      bitsPerSecond: 128000
    }
  )

  React.useEffect(() => {
    if (recordingBlob) {
      onRecordingComplete(recordingBlob)
    }
  }, [recordingBlob, onRecordingComplete])

  React.useEffect(() => {
    if (isRecording && recordingTime >= maxRecordingTime) {
      stopRecording()
      onIsRecording(false)
    }
  }, [isRecording, recordingTime, maxRecordingTime, stopRecording, onIsRecording])

  const onRecordButtonToggle = () => {
    if (isRecording) {
      stopRecording()
      onIsRecording(false)
    } else {
      startRecording()
      onIsRecording(true)
    }
  }

  return (
    <Container>
      {children}
      <ControlButton onClick={onRecordButtonToggle} disabled={disabled}>
        <CircularProgressbarWithChildren value={recordingTime} maxValue={maxRecordingTime}>
          <FontAwesomeIcon icon={faMicrophone} color={isRecording ? 'red' : 'black'} />
        </CircularProgressbarWithChildren>
      </ControlButton>
    </Container>
  )
}
